import React, { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { Button, makeStyles } from "@material-ui/core";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import AnnouncementModal from "../../components/AnnouncementModal";
import AnnouncementCard from "../../components/AnnouncementCard";
import openSocket from "../../services/socket-io";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  list: {
    flex: 1,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    paddingBottom: theme.spacing(2),
    ...theme.scrollbarStyles,
  },
  empty: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
}));

const Announcements = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const isAdmin = user.profile === "admin";
  const [announcements, setAnnouncements] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [modal, setModal] = useState({ open: false, announcement: null });
  const [toDelete, setToDelete] = useState(null);

  const fetchAll = async () => {
    try {
      const { data } = await api.get("/announcements");
      setAnnouncements(data);
    } catch (err) {
      toastError(err);
    }
    setLoaded(true);
  };

  useEffect(() => {
    fetchAll();
    const socket = openSocket();
    socket.on("announcement", fetchAll);
    return () => socket.disconnect();
  }, []);

  const handleDelete = async () => {
    try {
      await api.delete(`/announcements/${toDelete.id}`);
      toast.success(i18n.t("announcements.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setToDelete(null);
  };

  return (
    <MainContainer>
      <AnnouncementModal
        open={modal.open}
        announcement={modal.announcement}
        onClose={() => setModal({ open: false, announcement: null })}
      />
      <ConfirmationModal
        title={i18n.t("announcements.confirmDelete")}
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={handleDelete}
      >
        {toDelete?.title}
      </ConfirmationModal>
      <MainHeader>
        <Title>{i18n.t("announcements.title")}</Title>
        <MainHeaderButtonsWrapper>
          {isAdmin && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setModal({ open: true, announcement: null })}
            >
              {i18n.t("announcements.buttons.add")}
            </Button>
          )}
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <div className={classes.list}>
        {loaded && announcements.length === 0 && (
          <div className={classes.empty}>{i18n.t("announcements.empty")}</div>
        )}
        {announcements.map((a) => (
          <AnnouncementCard
            key={a.id}
            announcement={a}
            onEdit={isAdmin ? (item) => setModal({ open: true, announcement: item }) : undefined}
            onDelete={isAdmin ? setToDelete : undefined}
          />
        ))}
      </div>
    </MainContainer>
  );
};

export default Announcements;
